export function countPhrase(count, singular, plural = `${singular}s`) {
  const value = Number(count) || 0;
  return `${value} ${value === 1 ? singular : plural}`;
}

export function relativeMoment(value, now = Date.now()) {
  if (!value) return { label: "", sentence: "No reviews yet", absolute: "" };
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return { label: "", sentence: "No reviews yet", absolute: "" };
  const absolute = date.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
  const delta = Math.max(0, now - date.getTime());
  const minutes = Math.round(delta / 60000);
  if (minutes < 1) return { label: "just now", sentence: "Reviewed just now", absolute };
  if (minutes < 60) return { label: `${minutes}m ago`, sentence: `Reviewed ${minutes}m ago`, absolute };
  const hours = Math.round(minutes / 60);
  if (hours < 24) return { label: `${hours}h ago`, sentence: `Reviewed ${hours}h ago`, absolute };
  const start = new Date(now);
  start.setHours(0, 0, 0, 0);
  const day = new Date(date);
  day.setHours(0, 0, 0, 0);
  const dayDelta = Math.round((start.getTime() - day.getTime()) / 86400000);
  if (dayDelta === 1) return { label: "yesterday", sentence: "Reviewed yesterday", absolute };
  if (dayDelta < 7) return { label: `${dayDelta}d ago`, sentence: `Reviewed ${dayDelta}d ago`, absolute };
  const printed = date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  return { label: printed, sentence: `Reviewed ${printed}`, absolute };
}

export function smallestPackage(packages) {
  const rows = (packages || []).filter((item) => Number(item?.credits) > 0 && item?.id);
  if (!rows.length) return null;
  return [...rows].sort((left, right) => Number(left.credits) - Number(right.credits) || Number(left.priceCents) - Number(right.priceCents))[0];
}

export function packagePrice(cents) {
  const dollars = Number(cents) / 100;
  if (!Number.isFinite(dollars)) return "";
  return Number.isInteger(dollars) ? `$${dollars}` : `$${dollars.toFixed(2)}`;
}

export function packageActionLabel(pack) {
  if (!pack) return "Add reviews";
  return `Add ${pack.credits} reviews — ${packagePrice(pack.priceCents)}`;
}

export function guardrailCopy(overview) {
  const setup = overview?.setup || {};
  const count = Number(setup.repositoryCount) || 0;
  if (count === 0) {
    return {
      title: "Connect your first codebase",
      badge: "",
      tone: "setup",
      body: "Add a public repository instantly, or connect GitHub for private repositories and pull-request Checks.",
      showPipeline: false,
    };
  }
  const privateOnly = setup.privateRepositoryCount > 0 && setup.publicRepositoryCount === 0;
  if (setup.installationState === "suspended" || (privateOnly && setup.installationState !== "active")) {
    return {
      title: "Reconnect GitHub",
      badge: "PAUSED",
      tone: "warning",
      body: "Private reviews and GitHub Check publication are paused until GitHub access is restored.",
      showPipeline: true,
    };
  }
  if (setup.publicationPossible) {
    return {
      title: "Product review is active",
      badge: "ACTIVE",
      tone: "live",
      body: `RenderRepo is learning from ${countPhrase(count, "codebase")} and reviewing changes against what your product is supposed to do.`,
      support: "RenderRepo reviews pull requests and publishes findings. It does not block merges.",
      showPipeline: true,
    };
  }
  if (setup.reviewReady) {
    return {
      title: "Reviews are ready",
      badge: "SETUP NEEDED",
      tone: "setup",
      body: "Reviews work now. GitHub Check publication still needs setup before findings can appear on the pull request.",
      support: "RenderRepo reviews pull requests and publishes findings. It does not block merges.",
      showPipeline: true,
    };
  }
  return {
    title: "Connect GitHub",
    badge: "SETUP NEEDED",
    tone: "setup",
    body: "Connect GitHub before RenderRepo can review private code or publish Checks.",
    showPipeline: true,
  };
}

export function githubMetric(setup) {
  if (setup?.installationState === "suspended") return "GitHub access suspended";
  if (setup?.installationState === "active" && setup?.checksWrite === false) return "Reviews work · GitHub Checks need setup";
  if (setup?.installationState === "active") return "GitHub connected";
  if (setup?.reviewReady) return "Public reviews available";
  return "GitHub needs connection";
}

export function creditMetric(credits) {
  if (!credits) return "Review balance unavailable";
  if (!credits.billingEnabled) return "Pilot reviews active";
  return `${countPhrase(credits.available, "review")} left`;
}

export function connectedSupport(setup) {
  if (setup?.installationState === "active") return "GitHub App active";
  if (setup?.reviewReady) return "Public reviews available";
  return "GitHub connection needed";
}

export function learningSupport(brain) {
  if (!brain) return "Product context is still loading";
  if (brain.pendingConfirmation > 0) return `${countPhrase(brain.pendingConfirmation, "needs confirmation", "need confirmation")}`;
  if (brain.openConflicts > 0 || brain.unsafeInferred > 0) return "Some product rules need a decision";
  if (brain.activeRules > 0) return "Product context is up to date";
  return "Learning from connected evidence";
}

export function reviewingValue(overview) {
  if (overview?.setup?.publicationPossible || overview?.publication?.status === "ready") return "GitHub Checks publishing";
  if (overview?.setup?.reviewReady || overview?.publication?.status === "manual") return "Manual reviews ready";
  return "Publication needs setup";
}

/** @deprecated Prefer reviewingValue. */
export function protectingValue(overview) {
  return reviewingValue(overview);
}

export function attentionItems(overview) {
  const items = [];
  const credits = overview?.credits;
  const brain = overview?.brain;
  const reviews = overview?.reviews;
  const setup = overview?.setup || {};
  if (credits?.billingEnabled && credits.available === 0) {
    items.push({ id: "credits", label: "You’re out of reviews", href: "#review-balance", action: "Add reviews" });
  }
  if ((reviews?.failedPublication || 0) > 0) {
    items.push({
      id: "publication",
      label: reviews.failedPublication === 1 ? "A GitHub Check needs attention" : `${reviews.failedPublication} GitHub Checks need attention`,
      href: "/settings#github",
      action: "Review findings",
    });
  }
  if ((reviews?.needsAttention || 0) > 0) {
    items.push({
      id: "reviews",
      label: reviews.needsAttention === 1 ? "1 review needs a decision" : `${reviews.needsAttention} reviews need a decision`,
      href: "/reviews?filter=attention",
      action: "Review findings",
    });
  }
  if ((brain?.openConflicts || 0) > 0) {
    items.push({
      id: "conflicts",
      label: brain.openConflicts === 1 ? "1 product-rule conflict needs resolution" : `${brain.openConflicts} product-rule conflicts need resolution`,
      href: "/brain?lens=attention",
      action: "Review product rules",
    });
  }
  if ((brain?.unsafeInferred || 0) > 0) {
    items.push({
      id: "unsafe",
      label: brain.unsafeInferred === 1 ? "1 product rule needs a closer look" : `${brain.unsafeInferred} product rules need a closer look`,
      href: "/brain?lens=attention",
      action: "Review product rules",
    });
  }
  if ((brain?.pendingConfirmation || 0) > 0) {
    items.push({
      id: "confirm",
      label: brain.pendingConfirmation === 1 ? "1 product rule needs confirmation" : `${brain.pendingConfirmation} product rules need confirmation`,
      href: "/brain?lens=attention",
      action: "Review product rules",
    });
  }
  if (setup.installationState === "suspended") {
    items.push({ id: "suspended", label: "GitHub access is suspended", href: "/settings#github", action: "Open GitHub settings" });
  } else if (setup.installationState !== "active" && setup.repositoryCount > 0 && !setup.reviewReady) {
    items.push({ id: "github", label: "GitHub needs connection", href: "/settings#github", action: "Open GitHub settings" });
  } else if (setup.installationState === "active" && setup.checksWrite === false) {
    items.push({ id: "checks", label: "GitHub Checks need permission", href: "/settings#github", action: "Open GitHub settings" });
  }
  return items;
}

export function attentionAction(items) {
  const finding = items.find((item) => item.id === "reviews" || item.id === "publication");
  if (finding) return { label: "Review findings", href: finding.href };
  const rules = items.find((item) => item.id === "confirm" || item.id === "conflicts" || item.id === "unsafe");
  if (rules) return { label: "Review product rules", href: rules.href };
  const github = items.find((item) => item.id === "github" || item.id === "suspended" || item.id === "checks");
  if (github) return { label: github.action, href: github.href };
  return null;
}

export function codebaseStatusLabel(status) {
  if (status === "protected") return "Reviews active";
  if (status === "learning") return "Learning";
  if (status === "needs_setup") return "Needs setup";
  if (status === "attention") return "Attention";
  return "Not reviewed";
}

export function activeRuleCount(row) {
  return Number(row?.confirmedRules || 0) + Number(row?.safeInferredRules || 0);
}
